import React, { useEffect, useMemo, useState, useRef } from "react";
import { doc, getDoc, collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, fns } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";
import { instructionalWeek } from "../lib/pace.js";

const pad = (n) => String(n).padStart(2, "0");

const LADDER = [
  ["1 · The Companion", "Instant, Socratic, below on this page. It will not do the work; it will find the question you're avoiding."],
  ["2 · Your pod", "Five people who owe each other critique within 48 hours. Most blockers die here."],
  ["3 · Clinic", "Weekly drop-in topic rooms — book a seat below. Bring the one-paragraph question."],
  ["4 · Studio Master 1:1", "Fifteen minutes, bookable below. For what the Clinic couldn't reach."],
  ["5 · Studio Director", "The exception path — pod conflict, personal circumstances, an adjustment. Ask your Studio Master to open it, or email the address in your welcome pack."],
];

export default function Help() {
  const s = useSession();
  const [cohort, setCohort] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [log, setLog] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [commit, setCommit] = useState(["", "", ""]);
  const [myCommits, setMyCommits] = useState([]);
  const [pairing, setPairing] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!s.profile?.cohortId) return;
    getDoc(doc(db, "cohorts", s.profile.cohortId)).then((d) => d.exists() && setCohort(d.data()));
    const q = query(collection(db, "sessions"), where("cohortId", "==", s.profile.cohortId), orderBy("startTime", "asc"));
    return onSnapshot(q, (snap) => setSessions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [s.profile?.cohortId]);

  useEffect(() => {
    if (!s.user) return;
    const q = query(collection(db, "commitments"), where("residentUid", "==", s.user.uid), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => setMyCommits(snap.docs.map((d) => d.data())));
  }, [s.user]);

  useEffect(() => {
    if (!s.profile?.cohortId || !s.user) return;
    return onSnapshot(doc(db, "pairings", s.profile.cohortId), (d) => {
      if (!d.exists()) return setPairing(null);
      const p = d.data().pairs?.find((x) => x.a === s.user.uid || x.b === s.user.uid);
      setPairing(p ? (p.a === s.user.uid ? p.b : p.a) : null);
    });
  }, [s.profile?.cohortId, s.user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [log, busy]);

  const now = useMemo(() => instructionalWeek(cohort?.startDate), [cohort]);
  const briefId = `A${pad(now.week === 0 ? 0 : now.week)}`;

  const upcoming = sessions.filter((x) => new Date(x.startTime) > new Date()
    && (!x.clusterId || x.clusterId === s.profile?.clusterId)
    && (!x.podId || x.podId === s.profile?.podId));

  const ask = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft(""); setMsg(null);
    const next = [...log, { role: "user", content: text }];
    setLog(next); setBusy(true);
    try {
      const call = httpsCallable(fns, "askCompanion");
      const res = await call({ briefId, messages: next });
      setLog([...next, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMsg(e.message);
    } finally { setBusy(false); }
  };

  const book = async (sessionId, action) => {
    setMsg(null);
    try {
      const call = httpsCallable(fns, "bookSession");
      await call({ sessionId, action });
    } catch (e) { setMsg(e.message); }
  };

  const fileCommitments = async () => {
    if (commit.some((c) => c.trim().length < 8)) return setMsg("Three specific commitments — each one a change you can be checked on next week.");
    await addDoc(collection(db, "commitments"), {
      residentUid: s.user.uid, cohortId: s.profile?.cohortId ?? null, podId: s.profile?.podId ?? null,
      week: now.week, items: commit.map((c) => c.trim()), createdAt: serverTimestamp(),
    });
    setCommit(["", "", ""]); setMsg("Filed. Your pod checks these at the next crit.");
  };

  const TYPE = { clinic: "Clinic", oneonone: "1:1", crit: "Crit", visiting: "VISITING CRITIC" };

  return (
    <div style={{ maxWidth: 860 }}>
      <Eyebrow>Help · the escalation ladder</Eyebrow>
      <h1 style={{ fontSize: 24, marginTop: 4 }}>Stuck is normal. Stuck alone is optional.</h1>
      <div className="card" style={{ margin: "14px 0" }}>
        {LADDER.map(([t, d], i) => (
          <div key={i} className={i < LADDER.length - 1 ? "crit" : ""} style={{ padding: "8px 0" }}>
            <strong>{t}.</strong> <span style={{ fontSize: 14 }}>{d}</span>
          </div>
        ))}
      </div>

      {/* Companion */}
      <div className="card" style={{ marginBottom: 14 }}>
        <Eyebrow>The Companion · knows you're on {briefId}</Eyebrow>
        <div style={{ maxHeight: 300, overflowY: "auto", margin: "10px 0" }}>
          {log.length === 0 && (
            <p style={{ color: "var(--graphite)", fontSize: 14 }}>
              It won't design for you or write anything you'll submit. It will ask the next useful question.
            </p>
          )}
          {log.map((m, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <Eyebrow style={{ color: m.role === "user" ? "var(--indigo)" : "var(--verdigris)" }}>
                {m.role === "user" ? "You" : "Companion"}
              </Eyebrow>
              <div style={{ whiteSpace: "pre-wrap", fontSize: 15 }}>{m.content}</div>
            </div>
          ))}
          {busy && <p style={{ color: "var(--graphite)", fontStyle: "italic" }}>Thinking…</p>}
          <div ref={endRef} />
        </div>
        <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }}
          placeholder="Ask about the work, not for the work." />
        <div className="row between" style={{ marginTop: 6 }}>
          <span style={{ fontSize: 11, color: "var(--graphite)" }}>Ctrl/Cmd + Enter</span>
          <button className="btn" onClick={ask} disabled={busy}>Ask</button>
        </div>
      </div>

      {/* Sessions */}
      <div className="card" style={{ marginBottom: 14 }}>
        <Eyebrow>Book a human</Eyebrow>
        {upcoming.length === 0 && (
          <p style={{ color: "var(--graphite)", fontSize: 14, marginTop: 6 }}>
            No sessions posted yet. Clinics are weekly once term runs — if this stays empty in term, raise it with your Studio Master; that is itself an escalation.
          </p>
        )}
        {upcoming.map((x) => {
          const mine = (x.attendeeUids ?? []).includes(s.user.uid);
          const full = x.capacity && (x.attendeeUids ?? []).length >= x.capacity;
          return (
            <div key={x.id} className="crit row between wrapflex" style={{ gap: 8 }}>
              <div>
                <span className="pill" style={x.type === "visiting" ? { color: "var(--bronze)", borderColor: "var(--bronze)", fontWeight: 700 } : {}}>{TYPE[x.type] ?? x.type}</span>{" "}
                <strong>{x.title}</strong>
                {x.guestName && <span style={{ fontSize: 13 }}> — with <strong>{x.guestName}</strong>{x.guestBio ? `, ${x.guestBio}` : ""}</span>}
                <div style={{ fontSize: 13, color: "var(--graphite)" }}>
                  {new Date(x.startTime).toLocaleString()} · {x.durationMin} min · {x.hostName ?? "Staff"} ·{" "}
                  {(x.attendeeUids ?? []).length}{x.capacity ? `/${x.capacity}` : ""} booked
                  {mine && x.meetingUrl && <> · <a href={x.meetingUrl} target="_blank" rel="noreferrer">join link</a></>}
                </div>
              </div>
              <button className={`btn ${mine ? "ghost" : ""}`} disabled={!mine && full}
                onClick={() => book(x.id, mine ? "cancel" : "book")}>
                {mine ? "Cancel" : full ? "Full" : "Book"}
              </button>
            </div>
          );
        })}
      </div>

      {pairing && (
        <div className="card" style={{ marginBottom: 14, borderColor: "var(--indigo)" }}>
          <Eyebrow style={{ color: "var(--indigo)" }}>Your Stranger Test partner</Eyebrow>
          <p style={{ fontSize: 14, marginTop: 6 }}>
            Resident <span className="mono">{pairing.slice(0, 10)}</span> — from outside your pod, on purpose.
            You test their system cold from its documentation; they test yours. Log every question you have
            to ask: more than two fails the gate.
          </p>
        </div>
      )}

      {/* Crit commitments */}
      <div className="card">
        <Eyebrow>Crit commitments · the three changes</Eyebrow>
        <p style={{ fontSize: 13, color: "var(--graphite)", margin: "4px 0 8px" }}>
          Filed after every crit, checked at the next one. Permanent once filed — a commitment you can edit later is a wish.
        </p>
        {commit.map((c, i) => (
          <input key={i} type="text" value={c} placeholder={`Change ${i + 1}`} style={{ marginTop: 6 }}
            onChange={(e) => setCommit(commit.map((x, j) => (j === i ? e.target.value : x)))} />
        ))}
        <button className="btn" style={{ marginTop: 10 }} onClick={fileCommitments}>File this week's three</button>
        {myCommits[0] && (
          <div style={{ marginTop: 14, padding: 10, background: "#fff", borderLeft: "3px solid var(--indigo)" }}>
            <Eyebrow>Being checked this week (week {myCommits[0].week})</Eyebrow>
            <ol style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 14 }}>
              {myCommits[0].items.map((it, i) => <li key={i}>{it}</li>)}
            </ol>
          </div>
        )}
      </div>

      {msg && <div className="warn" style={{ marginTop: 12 }}>{msg}</div>}
    </div>
  );
}
